import { createAsyncThunk } from '@reduxjs/toolkit'
import { getNotificationMessage } from 'store'
import {
  setMeXmppInitialState,
  changeNumUnread,
  clearXmppPushNotification,
  messageReceived,
  syncStateChanged
} from 'store/chat/slice'
import { usersApi } from 'store/users/api'
import { REJECTED } from 'utils/constants'
import * as sdk from 'matrix-js-sdk'
import {
  // getParsedPassword,
  getParsedPassword2
} from 'utils/helpers'
import InitChat from 'components/Messenger/chatui/chat_init'
import { getMatrixClient, setMatrixClient } from 'components/Messenger/Matrix/clientStore'

const isLocalEnv = (process.env.REACT_APP_USE_LOCAL_XMP !== undefined && process.env.REACT_APP_USE_LOCAL_XMP === 'true')

let refreshTimerId = null

const switchTestUser = () => {
  let res = ''
  switch (window.testFlip) {
  case 'test01@localhost':
    res = 'test04@localhost'
    break
  case 'test04@localhost':
    res = 'test01@localhost'
    break
  default:
    res = 'test04@localhost'
  }
  window.testFlip = res
  return res
}

export const getChatCredentials = createAsyncThunk(
  'chat/getChatCredentials',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const response = await usersApi.getChatCredentials()

      return response.data
    } catch (error) {
      await dispatch(
        getNotificationMessage({
          type: REJECTED,
          message: 'Get Chat Credentials Error'
        })
      )
      return rejectWithValue(error.message)
    }
  }
)

const doConnectChat = async (chat) => {
  // if invalid data then exit
  if (chat.chatCredentials === null || !chat.chatCredentials.username
    || !chat.chatCredentials.password) {
    return Promise.resolve()
  }
  // const password = getParsedPassword(chat.chatCredentials.password)
  const password2 = await getParsedPassword2(chat.chatCredentials.password)
  const discover_connection_methods = false
  const ws_url = isLocalEnv ? 'ws://localhost:5280/ws/' : `wss://${process.env.REACT_APP_XMPP_CHAT_URL}:5443/ws`
  const jid = isLocalEnv ? switchTestUser() : `${chat.chatCredentials.username}@${process.env.REACT_APP_XMPP_CHAT_URL}`
  const pass = isLocalEnv ? 'pass' : password2
  if (isLocalEnv) {
    console.log(`connecting to chat with jid: "${jid}"`)
  }
  return InitChat(jid, pass, discover_connection_methods, undefined, ws_url)
}

export const LogoutFromChat = createAsyncThunk(
  'chat/LogoutFromChat',
  async (_, { rejectWithValue, dispatch, getState }) => {
    try {
      const { chat } = getState()
      await dispatch(changeNumUnread(0))
      await dispatch(clearXmppPushNotification())
      if (!chat.meXmppIsOnline) {
        return dispatch(setMeXmppInitialState())
      }
      const notifyUiAfterLogoutDone = () => new Promise((resolve) => {
        window.addEventListener('custom-chat-logout-complete', resolve, { once: true })
      })

      const event = new CustomEvent('custom-chat-logout')
      window.dispatchEvent(event)
      await notifyUiAfterLogoutDone()
      return dispatch(setMeXmppInitialState())
    } catch (error) {
      await dispatch(
        getNotificationMessage({
          type: REJECTED,
          message: 'Login chat user error'
        })
      )
      return rejectWithValue(error.message)
    }
  }
)

export const ConnectToChat = createAsyncThunk(
  'chat/ConnectToChat',
  async (_, { rejectWithValue, dispatch, getState }) => {
    try {
      const { chat } = getState()
      if (chat.meXmppIsOnline) {
        await dispatch(LogoutFromChat())
      }

      await dispatch(setMeXmppInitialState())
      return doConnectChat(chat)
    } catch (error) {
      await dispatch(
        getNotificationMessage({
          type: REJECTED,
          message: 'Login chat user error'
        })
      )
      return rejectWithValue(error.message)
    }
  }
)

// Matrix client functions

// log in to matrix and get credentials (no longer needed since it is handled in the backend)
export const loginToMatrix = createAsyncThunk(
  'matrix/login',
  async ({ baseUrl, username, password }, { rejectWithValue }) => {
    try {
      const tempClient = sdk.createClient({ baseUrl })
      const res = await tempClient.loginRequest('m.login.password', {
        user: username,
        password
      })
      return {
        baseUrl,
        accessToken: res.access_token,
        userId: res.user_id,
        deviceId: res.device_id
      }
    } catch (err) {
      return rejectWithValue(err.message)
    }
  }
)

export const refreshMatrixToken = createAsyncThunk(
  'matrix/refreshToken',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const client = getMatrixClient()
      if (!client) throw new Error('No active Matrix client')

      const currentRefreshToken = client.getRefreshToken?.()
      if (!currentRefreshToken) throw new Error('No refresh token available')

      const response = await client.refreshToken(currentRefreshToken)

      // client.http.opts.accessToken = response.access_token
      client.setAccessToken(response.access_token)

      // Re-schedule the next refresh using the new expiry
      dispatch(
        // eslint-disable-next-line no-use-before-define
        scheduleTokenRefresh({
          expiresInMs: response.expires_in_ms
        })
      )

      return {
        accessToken: response.access_token,
        refreshToken: response.refresh_token
      }
    } catch (err) {
      return rejectWithValue(err.message)
    }
  }
)

export const scheduleTokenRefresh = createAsyncThunk(
  'matrix/scheduleTokenRefresh',
  async ({ expiresInMs }, { dispatch }) => {
    if (refreshTimerId) clearTimeout(refreshTimerId)

    const refreshInMs = Math.max(expiresInMs - 30000, 5000)

    refreshTimerId = setTimeout(() => {
      dispatch(refreshMatrixToken())
    }, refreshInMs)

    return true
  }
)

export const clearScheduledTokenRefresh = createAsyncThunk(
  'matrix/clearScheduledTokenRefresh',
  async () => {
    if (refreshTimerId) {
      clearTimeout(refreshTimerId)
      refreshTimerId = null
    }
    return true
  }
)

//  start client
export const startMatrixClient = createAsyncThunk(
  'matrix/startClient',
  async (credentials, { dispatch, rejectWithValue }) => {
    try {
      const client = sdk.createClient({
        baseUrl: credentials.baseUrl,
        accessToken: credentials.accessToken,
        userId: credentials.userId,
        deviceId: credentials.deviceId,
        refreshToken: credentials.refreshToken,
        tokenRefreshFunction: async (refreshToken) => {
          const response = await client.refreshToken(refreshToken)
          return {
            accessToken: response.access_token,
            refreshToken: response.refresh_token
          }
        }
      })

      setMatrixClient(client)

      // Wire up live events -> dispatch plain actions as they arrive
      client.on('Room.timeline', (event, room) => {
        if (event.getType() === 'm.room.message') {
          dispatch(
            messageReceived({
              roomId: room.roomId,
              sender: event.getSender(),
              body: event.getContent().body,
              ts: event.getTs()
            })
          )
        }
      })

      client.on('sync', (state) => {
        dispatch(syncStateChanged(state))
      })

      await client.startClient({ initialSyncLimit: 20 })

      // Wait for the first PREPARED sync before resolving the thunk
      await new Promise((resolve) => {
        client.once('sync', (state) => {
          if (state === 'PREPARED') resolve()
        })
      })

      if (credentials.expiresInMs) {
        dispatch(scheduleTokenRefresh({ expiresInMs: credentials.expiresInMs }))
      }

      const rooms = client.getRooms().map((r) => ({
        roomId: r.roomId,
        name: r.name
      }))

      return { rooms }
    } catch (err) {
      return rejectWithValue(err.message)
    }
  }
)

// send a message
export const sendMessage = createAsyncThunk(
  'matrix/sendMessage',
  async ({ roomId, text }, { rejectWithValue }) => {
    try {
      const client = getMatrixClient()
      if (!client) throw new Error('Matrix client not started')
      await client.sendTextMessage(roomId, text)
      return { roomId, text }
    } catch (err) {
      return rejectWithValue(err.message)
    }
  }
)

// stop client
export const stopMatrixClient = createAsyncThunk(
  'matrix/stopClient',
  async (_, { dispatch }) => {
    dispatch(clearScheduledTokenRefresh())
    const client = getMatrixClient()
    if (client) client.stopClient()
    setMatrixClient(null)
    return true
  }
)
